import { Slot } from '@radix-ui/react-slot'
import { cn } from '@sistemas/ui'
import { cva, type VariantProps } from 'class-variance-authority'
import * as React from 'react'

/**
 * Button — shadcn/ui, estilo `default`, tokens Flexibase Strict.
 * Densidade do ecossistema: altura padrão h-8 (não h-10), texto 12–13px.
 */
const buttonVariants = cva(
  'inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-md text-[13px] font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:shrink-0',
  {
    variants: {
      variant: {
        default: 'bg-primary text-primary-foreground hover:bg-primary/90',
        destructive:
          'bg-destructive text-destructive-foreground hover:bg-destructive/90',
        outline: 'border border-input bg-background hover:bg-accent hover:text-accent-foreground',
        secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
        ghost: 'hover:bg-accent hover:text-accent-foreground',
        link: 'text-primary underline-offset-4 hover:underline',
      },
      // Celular (Onda 6.3): alvo de toque de 44 px em todo botão. `min-*` e não
      // `h-*`, para não brigar com a altura que cada tela passa por classe.
      size: {
        default: 'h-8 px-3 py-1 max-md:min-h-11',
        sm: 'h-7 rounded-md px-2 text-[12px] max-md:min-h-11',
        lg: 'h-9 rounded-md px-4 max-md:min-h-11',
        icon: 'h-8 w-8 max-md:min-h-11 max-md:min-w-11',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button'
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    )
  },
)
Button.displayName = 'Button'

export { Button, buttonVariants }
